import * as React from "react";
import styled from "styled-components";

import { select } from "@hubper/theme";
import { Checkbox } from "@hubper/core-ui/elements";
import { ROW_HEIGHT } from "../constants";

import { GridChildComponentProps } from "react-window";
import { getColumnValue, oneOfKeysChanged } from "../../util";

import Context, { ContextType, Config, Actions } from "../../context";

const Base = styled.div`
  border-bottom: 1px solid hsla(0, 0%, 90%, 1);
  display: flex;
  align-items: center;
  padding: 12px;
  box-sizing: border-box;
  overflow: hidden;
  font-size: ${select.fontSize(-1)};
  color: ${select.color(["grey", 9])};
  height: ${ROW_HEIGHT}px;
  font-family: ${select.font("Lato")};
  position: relative;
`;

const Placeholder = styled.div`
  width: 80%;
  height: 16px;
  background-color: #ececec;
  border-radius: 3px;
`;

const isEven = (num: number) => num % 2 === 0;

function getDisplay(
  value: any,
  row: any,
  t: any,
  config: Config,
  width: number
): React.ReactNode {
  // column of type 'enum' gets little special treatment here
  if (config.type === "enum" && !config.render) {
    // column has no value, there's no point to render anything
    if (!value) {
      return "";
    }

    // get the rendered value of the map
    const result =
      typeof config.map === "function"
        ? config.map(t)[value]
        : config.map[value];
    return result || "";
  } else if (config.render) {
    // render as component
    return React.createElement(config.render as any, { value, row, t, width });
  } else if (typeof value !== "string" && value && value.toString) {
    // try to get the string representation;
    return value.toString();
  } else if (typeof value === "string") {
    // default behaviour -> just the string
    return value;
  }

  // edge cases -> empty cell
  return "";
}

function isActionColumn(
  columnConfigs: Config[],
  hasBulkSelect: boolean,
  columnIndex: number
) {
  let amountOfColumns = columnConfigs.length;
  if (hasBulkSelect) {
    amountOfColumns++;
  }

  return columnIndex === amountOfColumns;
}

function isHoverRelevant(prev: CellProps, next: CellProps) {
  if (
    prev.hoveredRowIndex === prev.rowIndex &&
    next.hoveredRowIndex !== next.rowIndex
  ) {
    return true;
  }
  if (
    prev.hoveredRowIndex !== prev.rowIndex &&
    next.hoveredRowIndex === next.rowIndex
  ) {
    return true;
  }

  return false;
}

type GetCellTypeArgs = {
  columnConfig: Config[];
  hasBulkSelect: boolean;
  columnIndex: number;
  actions?: Actions;
  isLoading: boolean;
};

function getCellType({
  columnConfig,
  columnIndex,
  hasBulkSelect,
  actions,
  isLoading
}: GetCellTypeArgs) {
  if (hasBulkSelect && columnIndex === 0) {
    return "BULK_SELECT";
  }
  if (isLoading) {
    return "LOADING";
  }
  if (actions && isActionColumn(columnConfig, hasBulkSelect, columnIndex)) {
    return "ACTION";
  }

  return "DEFAULT";
}

type CellProps = GridChildComponentProps & ContextType;

const Cell = React.memo(
  function Cell({
    columnIndex,
    rowIndex,
    style,
    data,
    rowColor1,
    rowColor2,
    columnConfig,
    hasBulkSelect,
    selectedRows,
    onSelectedRowsChange,
    actions,
    t,
    onRowClick,
    hoveredRowIndex,
    setHoveredRowIndex,
    isLoading
  }: GridChildComponentProps & ContextType) {
    const row = data[rowIndex];
    let display;

    const CELL_TYPE = getCellType({
      actions,
      columnConfig,
      columnIndex,
      hasBulkSelect,
      isLoading
    });

    switch (CELL_TYPE) {
      case "LOADING":
        {
          display = <Placeholder data-testid="cell-placeholder" />;
        }
        break;
      case "BULK_SELECT":
        {
          const selected = Boolean(selectedRows!.find(id => id === row.id));
          display = (
            <Checkbox
              value={selected}
              onClick={() => {
                if (selected) {
                  onSelectedRowsChange!(
                    selectedRows!.filter(id => id !== row.id)
                  );
                } else {
                  onSelectedRowsChange!(selectedRows!.concat(row.id));
                }
              }}
            />
          );
        }
        break;
      case "ACTION":
        {
          const Component = actions!.render;
          display = <Component row={row} />;
        }
        break;
      default: {
        const config = columnConfig[columnIndex + (hasBulkSelect ? -1 : 0)];
        const value = getColumnValue(row, config, t);
        display = getDisplay(value, row, t, config, style.width as number);
      }
    }

    let backgroundColor;
    if (hoveredRowIndex === rowIndex) {
      backgroundColor = "hsl(255, 0%, 90%)";
    } else if (isEven(rowIndex)) {
      backgroundColor = rowColor1;
    } else {
      backgroundColor = rowColor2;
    }

    return (
      <Base
        data-testid={`cell-${rowIndex}-${columnIndex}`}
        style={{
          ...style,
          backgroundColor,
          cursor: CELL_TYPE === "DEFAULT" && onRowClick ? "pointer" : "default",
          justifyContent: CELL_TYPE === "ACTION" ? "flex-end" : "stretch"
        }}
        onMouseEnter={() => setHoveredRowIndex(rowIndex)}
        onMouseLeave={() => setHoveredRowIndex(null)}
        onClick={
          CELL_TYPE === "DEFAULT" && onRowClick && !isLoading
            ? () => onRowClick(row)
            : undefined
        }
      >
        {display}
      </Base>
    );
  },
  (prev: CellProps, next: CellProps) => {
    if (
      oneOfKeysChanged(["columnConfig", "data", "selectedRows"], prev, next)
    ) {
      return false;
    }

    if (isHoverRelevant(prev, next)) {
      return false;
    }

    // column size / position changed
    if (
      prev.style.width !== next.style.width ||
      prev.style.left !== next.style.left
    ) {
      return false;
    }

    return true;
  }
);

// Wrapper that injects table-context into Cell
// Why not use context directly inside Cell? -> Performance...
// This way we can inspect whether relevant props changed, and thus
// whether we should re-render
class CellWrapper extends React.Component<GridChildComponentProps> {
  static contextType = Context;

  render() {
    return <Cell {...this.context} {...this.props} />;
  }
}

export default CellWrapper;
